import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, Alert, Stack, Box, Typography, IconButton,
  Avatar, InputAdornment, FormControl, InputLabel, Select, MenuItem,
  useTheme, CircularProgress
} from '@mui/material';
import { 
  Close as CloseIcon, 
  Person as PersonIcon, 
  Email as EmailIcon, 
  Lock as LockIcon,
  AdminPanelSettings as AdminIcon,
  PersonAdd as PersonAddIcon,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { createUser } from '../../api/adminApi';

const CreateUserDialog = ({ open, onClose, onUserCreated }) => {
    const theme = useTheme();
    const [formData, setFormData] = useState({ username: '', email: '', password: '', role: 'user' });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        const fieldName = name.replace('new-user-', '');
        setFormData({ ...formData, [fieldName]: value });
    };

    const handleClose = () => {
        setFormData({ username: '', email: '', password: '', role: 'user' });
        setError('');
        setLoading(false);
        setShowPassword(false);
        onClose();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        
        if (!formData.username || !formData.email || !formData.password) {
            setError('Please fill all fields.');
            return;
        }
        if (formData.password.length < 6) {
            setError('Password must be at least 6 characters long.');
            return;
        }
        
        setLoading(true);
        try {
            await createUser(formData);
            onUserCreated();
            handleClose();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to create user.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose} 
            maxWidth="sm" 
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    overflow: 'hidden',
                    boxShadow: '0 10px 40px rgba(102, 126, 234, 0.2)'
                }
            }}
        >
            {/* Compact Header */}
            <DialogTitle sx={{ 
                p: 0, 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white'
            }}>
                <Box sx={{ 
                    p: 3, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ 
                            bgcolor: 'rgba(255,255,255,0.2)', 
                            border: '2px solid rgba(255,255,255,0.3)',
                            width: 48,
                            height: 48
                        }}>
                            <PersonAddIcon />
                        </Avatar>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                Create New User
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                Add a new user to the system
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton 
                        onClick={handleClose}
                        sx={{ 
                            color: 'white',
                            bgcolor: 'rgba(255,255,255,0.1)',
                            '&:hover': { 
                                bgcolor: 'rgba(255,255,255,0.2)'
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            {/* Compact Form */}
            <Box
                component="form"
                onSubmit={handleSubmit}
                autoComplete="off"
                noValidate
            >
                <DialogContent sx={{ p: 3 }}>
                    {error && (
                        <Alert 
                            severity="error" 
                            sx={{ 
                                mb: 3,
                                borderRadius: '8px'
                            }}
                        >
                            {error}
                        </Alert>
                    )}

                    <Stack spacing={2.5}>
                        <TextField 
                            label="Username" 
                            name="new-user-username" 
                            value={formData.username} 
                            onChange={handleChange} 
                            fullWidth
                            disabled={loading}
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck="false"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <PersonIcon sx={{ color: '#667eea' }} />
                                    </InputAdornment>
                                ),
                                inputProps: {
                                    'data-form-type': 'other',
                                    'data-lpignore': 'true',
                                    'autoComplete': 'nope'
                                }
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#667eea',
                                    }
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                    color: '#667eea'
                                }
                            }}
                        />

                        <TextField 
                            label="Email Address" 
                            name="new-user-email" 
                            type="email" 
                            value={formData.email} 
                            onChange={handleChange} 
                            fullWidth
                            disabled={loading}
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck="false"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <EmailIcon sx={{ color: '#667eea' }} />
                                    </InputAdornment>
                                ),
                                inputProps: {
                                    'data-form-type': 'other',
                                    'data-lpignore': 'true',
                                    'autoComplete': 'nope'
                                }
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#667eea',
                                    }
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                    color: '#667eea'
                                }
                            }}
                        />

                        <TextField 
                            label="Password" 
                            name="new-user-password" 
                            type={showPassword ? 'text' : 'password'}
                            value={formData.password} 
                            onChange={handleChange} 
                            fullWidth
                            disabled={loading}
                            autoComplete="new-password"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck="false"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LockIcon sx={{ color: '#667eea' }} />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                            size="small"
                                            sx={{ color: '#667eea' }}
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                                inputProps: {
                                    'data-form-type': 'other',
                                    'data-lpignore': 'true',
                                    'autoComplete': 'new-password'
                                }
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#667eea',
                                    }
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                    color: '#667eea'
                                }
                            }}
                        />

                        <FormControl fullWidth disabled={loading}>
                            <InputLabel sx={{ '&.Mui-focused': { color: '#667eea' } }}>Role</InputLabel>
                            <Select
                                name="new-user-role"
                                value={formData.role}
                                onChange={handleChange}
                                label="Role"
                                autoComplete="off"
                                sx={{
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#667eea',
                                    }
                                }}
                                startAdornment={
                                    <InputAdornment position="start">
                                        <AdminIcon sx={{ color: '#667eea', ml: 1 }} />
                                    </InputAdornment>
                                }
                            >
                                <MenuItem value="user">User</MenuItem>
                                <MenuItem value="admin">Administrator</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>

                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 3 }}>
                        The user will receive login credentials and can start using the system immediately.
                    </Typography>
                </DialogContent>

                <DialogActions sx={{ p: 3, pt: 0, gap: 2 }}>
                    <Button 
                        onClick={handleClose}
                        variant="outlined"
                        disabled={loading}
                        sx={{ 
                            flex: 1,
                            py: 1.5,
                            fontWeight: 600,
                            textTransform: 'none',
                            borderColor: '#667eea',
                            color: '#667eea',
                            '&:hover': {
                                borderColor: '#5a67d8',
                                bgcolor: 'rgba(102, 126, 234, 0.04)'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        type="submit"
                        variant="contained"
                        disabled={loading}
                        sx={{ 
                            flex: 1,
                            py: 1.5,
                            fontWeight: 600,
                            textTransform: 'none',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #5a67d8 0%, #6b4e8a 100%)',
                                boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)'
                            }
                        }}
                    >
                        {loading ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={18} sx={{ color: 'white' }} />
                                <span>Creating...</span>
                            </Box>
                        ) : (
                            'Create User'
                        )}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    );
};

export default CreateUserDialog; 